import React, { useMemo } from 'react';
import priceHistoryData from '../ledger/priceHistory.json';

interface PriceRow {
  vendor: string;
  vendorNo: number | null;
  code: string;
  name: string;
  supplyPrice: number | null;
  supplyPriceVat: number | null;
  consumerPrice: number | null;
  shippingCost: number | null;
  photoCost: number | null;
  shoppingFee: number | null;
  tax: number | null;
  onlineMargin: number | null;
  onlineMarginRate: number | null;
}

const allRows = priceHistoryData as PriceRow[];

function formatNum(n: number | null): string {
  if (n == null) return '-';
  return Math.round(n).toLocaleString();
}
function formatPct(n: number | null): string {
  if (n == null) return '-';
  return (n * 100).toFixed(2) + '%';
}
function extractDateKey(code: string): number {
  const cleaned = code.toLowerCase();
  const m6 = cleaned.match(/\d{6}/g);
  if (m6 && m6.length > 0) {
    const last = m6[m6.length - 1];
    const y = parseInt(last.slice(0, 2), 10);
    const mo = parseInt(last.slice(2, 4), 10);
    const d = parseInt(last.slice(4, 6), 10);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return (2000 + y) * 10000 + mo * 100 + d;
  }
  const m4 = cleaned.match(/\b(20\d{2})\b/);
  if (m4) return parseInt(m4[1], 10) * 10000;
  return 0;
}

interface Props {
  vendorName: string;
  ledgerVendorName?: string; // 장부에서 다른 이름으로 등록된 경우 매핑 (예: 머드네농장 → 머드네블루베리)
}

export default function SupplyPriceTab({ vendorName, ledgerVendorName }: Props) {
  const matchName = ledgerVendorName || vendorName;
  const rows = useMemo(() => {
    const filtered = allRows.filter(r => r.vendor === matchName);
    return [...filtered].sort((a, b) => extractDateKey(b.code) - extractDateKey(a.code));
  }, [matchName]);

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-500">이 업체의 공급가 데이터가 없습니다.</p>
        <p className="text-xs text-gray-400 mt-2">
          장부에 등록된 이름이 다르면 VendorConfig에 <span className="font-mono">ledgerVendorName</span> 필드를 설정해주세요.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <strong className="text-gray-800">{vendorName}</strong>{vendorName !== matchName && <span className="text-gray-400"> (장부명: {matchName})</span>} · 총 <strong className="text-gray-800">{rows.length.toLocaleString()}</strong>건
        </div>
        <span className="text-xs text-gray-400">최신 코드 상단</span>
      </div>
      <div className="overflow-auto max-h-[calc(100vh-280px)]">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="[&_th]:border [&_th]:border-gray-200">
              <th rowSpan={2} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">코드</th>
              <th rowSpan={2} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">상품명</th>
              <th colSpan={2} className="text-center px-3 py-1 text-[10px] font-semibold text-gray-400 whitespace-nowrap">공급가</th>
              <th rowSpan={2} className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">소비자가</th>
              <th colSpan={4} className="text-center px-3 py-1 text-[10px] font-semibold text-gray-400 whitespace-nowrap">비용</th>
              <th colSpan={2} className="text-center px-3 py-1 text-[10px] font-semibold text-gray-400 whitespace-nowrap">온라인 마진</th>
            </tr>
            <tr className="[&_th]:border [&_th]:border-gray-200">
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">기본</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">VAT</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">배송</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">포토엽서</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">쇼핑수수료</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">세금</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">금액</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">마진율</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 500).map((r, i) => (
              <tr key={i} className="hover:bg-gray-50/70 transition-colors [&_td]:border [&_td]:border-gray-200">
                <td className="px-3 py-2 text-gray-500 font-mono text-xs whitespace-nowrap">{r.code || '-'}</td>
                <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{r.name}</td>
                <td className="px-3 py-2 text-gray-800 text-right whitespace-nowrap bg-yellow-50">{formatNum(r.supplyPrice)}</td>
                <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap bg-yellow-50">{formatNum(r.supplyPriceVat)}</td>
                <td className="px-3 py-2 text-gray-800 text-right whitespace-nowrap bg-orange-50">{formatNum(r.consumerPrice)}</td>
                <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.shippingCost)}</td>
                <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.photoCost)}</td>
                <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.shoppingFee)}</td>
                <td className="px-3 py-2 text-gray-600 text-right whitespace-nowrap">{formatNum(r.tax)}</td>
                <td className={`px-3 py-2 text-right whitespace-nowrap ${r.onlineMargin != null && r.onlineMargin < 0 ? 'text-red-500' : 'text-gray-800'}`}>{formatNum(r.onlineMargin)}</td>
                <td className={`px-3 py-2 text-right whitespace-nowrap ${r.onlineMarginRate != null && r.onlineMarginRate < 0 ? 'text-red-500' : 'text-gray-600'}`}>{formatPct(r.onlineMarginRate)}</td>
              </tr>
            ))}
            {rows.length > 500 && (
              <tr><td colSpan={11} className="px-4 py-3 text-center text-xs text-gray-400 bg-gray-50">처음 500건만 표시됨</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
